// Vendor Libs
import React, {useEffect} from 'react';

// Dish Tech React
import {CrudDataTable, Overlay} from '@dishtech-react/core';
import {useForm, useOverlay, useService} from '@dishtech-react/hooks';
import {GetRippleRoles, areRolesAuthorized} from '@dishtech-react/utils';

// Material UI
import {Card, CardMedia, TableCell, TableRow} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';

// Components
import NetworkForm from './network-form';

// Configs
import {serviceConfig} from '../../../config/service-config';
import {tableConfig} from '../../../config/table-config';
import Progress from '../helpers/progress';
import {vcaTypes} from '../helpers/tools';

const SERVICE_KEY = 'NETWORK';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: '300px',
  },
}));

const Network = () => {
  const rippleRoles = GetRippleRoles();
  const classes = useStyles();
  const {
    closeOverlay: closeCreateOverlay,
    open: isCreateOverlayOpen,
    openOverlay: openCreateOverlay,
    overlayId: createOverlayId,
  } = useOverlay({
    title: 'data-table',
  });
  const {
    closeOverlay: closeUpdateOverlay,
    open: isUpdateOverlayOpen,
    openOverlay: openUpdateOverlay,
    overlayId: updateOverlayId,
  } = useOverlay({
    title: 'data-table',
  });
  const {serviceContent, registerService, unregisterService} = useService(SERVICE_KEY);
  const {registerForm, setFieldValues} = useForm(serviceConfig, SERVICE_KEY, 'update');
  const {networks} = serviceContent;
  const {headerConfig, columns} = tableConfig[SERVICE_KEY];

  const getImages = rowData => rowData[columns.findIndex(({name}) => name === 'images')];

  const handleUpdateButtonClick = name => {
    registerForm().then(() =>
      setFieldValues(networks.find(network => network.name === name)).then(() => {
        openUpdateOverlay();
      })
    );
  };

  const renderExpandableRow = rowData => {
    const images = getImages(rowData)
      .split(' :: ')
      .filter(image => !image.endsWith('raw'));

    return (
      <>
        {images && images.length ? (
          <TableRow>
            <TableCell colSpan={6}>
              {images.map(image => (
                <Card className={classes.card} key={image}>
                  <CardMedia component="img" image={image} />
                </Card>
              ))}
            </TableCell>
          </TableRow>
        ) : null}
      </>
    );
  };

  useEffect(() => {
    if (areRolesAuthorized(rippleRoles, 'NETWORK', ['CREATE', 'DELETE', 'READ', 'UPDATE'])) {
      registerService();

      return () => unregisterService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defineVCATilecontent = providers => {
    const [adSupported, adFree, nullAdFree, noVcadefined] = vcaTypes;
    return providers
      ? providers?.map(provider => {
          let vcaStatus;
          if (provider?.adFree === undefined) {
            vcaStatus = nullAdFree;
          } else {
            vcaStatus = provider?.adFree ? adFree : adSupported;
          }

          return [provider.providerID, vcaStatus].join(' - ');
        })
      : noVcadefined;
  };

  return networks && networks.length ? (
    <>
      <CrudDataTable
        canCreate={areRolesAuthorized(rippleRoles, 'NETWORK', ['CREATE'])}
        canUpdate={areRolesAuthorized(rippleRoles, 'NETWORK', ['UPDATE'])}
        columnConfig={columns}
        data={networks.map(network => ({
          ...network,
          providers: defineVCATilecontent(network?.providers),
          images: [network.standardImageURL, network.widescreenImageURL, network.widescreenBackgroundImageURL].filter(
            Boolean
          ),
        }))}
        headerConfig={headerConfig}
        idKey="name"
        itemLabel="Network"
        options={{
          selectableRows: 'none',
          expandableRowsOnClick: false,
          expandableRows: true,
          renderExpandableRow,
        }}
        title="Network Management"
        onCreate={openCreateOverlay}
        onUpdate={handleUpdateButtonClick}
      />
      <Overlay id={createOverlayId} open={isCreateOverlayOpen} title="Create Network">
        <NetworkForm onCancel={closeCreateOverlay} onSave={closeCreateOverlay} />
      </Overlay>
      <Overlay id={updateOverlayId} open={isUpdateOverlayOpen} title="Update Network">
        <NetworkForm mode="update" onCancel={closeUpdateOverlay} onSave={closeUpdateOverlay} />
      </Overlay>
    </>
  ) : (
    <Progress title="Networks" />
  );
};

export default Network;
